import { Calendar, Mail, Phone, Star, Target, TrendingUp, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import { getCustomerPointsToNextTier } from "@/domains/customer/customers/utils/customer-utils";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/configuration";

interface CustomerMetricsDashboardProps {
  customer: CustomerDetailFragment;
  onCall?: () => void;
  onEmail?: () => void;
  onBookAppointment?: () => void;
}

export function CustomerMetricsDashboard({
  customer,
  onCall,
  onEmail,
  onBookAppointment,
}: CustomerMetricsDashboardProps) {
  const { t } = useTranslation("customers");
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const tierName = customer.currentTier?.name || t("list.item.noTier");
  const pointsToNextTier = getCustomerPointsToNextTier(customer);
  const progressPercentage =
    pointsToNextTier > 0 ? ((customer.totalPoints % 500) / 500) * 100 : 100;

  return (
    <div className="space-y-4">
      {/* Quick Actions - Mobile Only */}
      <div className="flex gap-2 sm:hidden">
        {customer.primaryPhone?.phone && (
          <Button
            variant="outline"
            onClick={onCall}
            className="flex items-center justify-center flex-1 p-2"
          >
            <Phone className="w-4 h-4" />
          </Button>
        )}
        {customer.email && (
          <Button
            variant="outline"
            onClick={onEmail}
            className="flex items-center justify-center flex-1 p-2"
          >
            <Mail className="w-4 h-4" />
          </Button>
        )}
        <Button onClick={onBookAppointment} className="flex items-center justify-center flex-1 p-2">
          <Calendar className="w-4 h-4" />
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div
        className={`grid grid-cols-2 gap-4 ${loyaltyEnabled ? "lg:grid-cols-5" : "lg:grid-cols-2"}`}
      >
        {/* Total Visits */}
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{customer.totalVisits}</div>
            <div className="text-sm text-muted-foreground">{t("detail.metrics.totalVisits")}</div>
          </CardContent>
        </Card>

        {/* Total Spent */}
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
              <PriceDisplay amount={customer.totalSpent} />
            </div>
            <div className="text-sm text-muted-foreground">{t("detail.metrics.totalSpent")}</div>
          </CardContent>
        </Card>

        {/* Loyalty Points */}
        {loyaltyEnabled && (
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Star className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mb-1">
                {customer.totalPoints}
              </div>
              <div className="text-sm text-muted-foreground">{t("detail.metrics.points")}</div>
            </CardContent>
          </Card>
        )}

        {/* Current Tier */}
        {loyaltyEnabled && (
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Trophy className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div
                className="text-lg font-bold mb-1 capitalize"
                style={{ color: customer.currentTier?.color || "#64748b" }}
              >
                {tierName}
              </div>
              <div className="text-sm text-muted-foreground">{t("detail.metrics.currentTier")}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {t("detail.metrics.level")} {customer.currentTier?.minPoints || 0}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tier Progress */}
        {loyaltyEnabled && pointsToNextTier > 0 && (
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <Target className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="relative w-12 h-12 mx-auto mb-2">
                <svg
                  className="w-12 h-12 transform -rotate-90"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <title>Visit goal progress</title>
                  {/* Background circle */}
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-muted-foreground/20"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - progressPercentage / 100)}`}
                    className="text-indigo-600 dark:text-indigo-400 transition-all duration-300"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                    {Math.round(progressPercentage)}%
                  </span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {pointsToNextTier} {t("detail.metrics.toNextTier")}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
